import { useIntl } from 'react-intl';

import {
	GetConfluenceTemplateCollectionDetailsTitleDescription,
	GetConfluenceTemplateCategoryTitleDescription,
} from './confluence-template-helpers';

import type { PreloadedQuery } from 'react-relay';
import type { OperationType } from 'relay-runtime';

interface DynamicTitleDescriptionResult {
	title: string | null;
	description: string | null;
}

// Extract title/description for Confluence template routes
function getConfluenceTemplateTitleDescription(
	routePath: string,
	queryRef: PreloadedQuery<OperationType> | undefined,
	intl: ReturnType<typeof useIntl>,
): DynamicTitleDescriptionResult {
	const result: DynamicTitleDescriptionResult = {
		title: null,
		description: null,
	};

	if (routePath.startsWith('/software/confluence/templates/collections/')) {
		const urlSlug = routePath.split('/').pop() ?? '';
		const response = GetConfluenceTemplateCollectionDetailsTitleDescription(urlSlug, queryRef);
		if (response?.title) {
			let title = response.title;

			// Apply title formatting for English locale
			if (intl.locale === 'en') {
				title = intl.formatMessage(
					{
						id: 'ConfluenceTemplatesCollectionDetailPage.pageTitle',
						defaultMessage: 'Free {title} collection | Confluence',
					},
					{ title },
				);
			} else {
				title +=
					' ' +
					intl.formatMessage({
						id: 'ConfluenceTemplatesCollectionDetailPage.title-suffix',
						defaultMessage: 'Collection | Atlassian',
					});
			}

			result.title = title;
			result.description = response.description;
		}
	} else if (routePath.startsWith('/software/confluence/templates/categories/')) {
		const urlSlug = routePath.split('/').pop() ?? '';
		const response = GetConfluenceTemplateCategoryTitleDescription(urlSlug, queryRef);
		if (response?.title) {
			let title = response.title;

			// Apply title formatting for English locale
			if (intl.locale === 'en') {
				title = intl.formatMessage(
					{
						id: 'ConfluenceTemplatesCategoryDetailPage.pageTitle',
						defaultMessage: 'Free {title} templates | Confluence',
					},
					{ title },
				);
			} else {
				title += ' | Atlassian';
			}

			result.title = title;
			result.description = response.description;
		}
	}

	return result;
}

// Update DOM with title and description
function updateDocumentMetadata(title: string | null, description: string | null): void {
	if (title && title !== document.title) {
		document.title = title;
	}
	if (description) {
		const metaDescription = document.querySelector('meta[name="description"]');
		if (metaDescription) {
			metaDescription.setAttribute('content', description);
		} else {
			// Create meta description if it doesn't exist
			const meta = document.createElement('meta');
			meta.name = 'description';
			meta.content = description;
			document.head.appendChild(meta);
		}
	}
}

export function useDynamicTitleDescription(
	location: Location,
	queryRef: PreloadedQuery<OperationType> | undefined,
) {
	try {
		// Must call hooks unconditionally
		const intl = useIntl();

		// Only run on client side and when we have queryRef
		if (typeof window === 'undefined' || !queryRef) {
			return;
		}

		const routePath = location.pathname;

		let result: DynamicTitleDescriptionResult = {
			title: null,
			description: null,
		};

		// Handle Confluence template routes
		if (routePath.startsWith('/software/confluence/templates/')) {
			result = getConfluenceTemplateTitleDescription(routePath, queryRef, intl);
		}

		// Update document metadata
		updateDocumentMetadata(result.title, result.description);
	} catch (error) {
		// Silently handle any errors in title/description extraction
		console.warn('Error updating dynamic title/description:', error);
	}
}
